import { getAwardShowLandingPublicPaths } from "@award-party/shared";
import { isClerkCaptchaPath } from "@/lib/clerk-captcha";

/**
 * Clerk `createRouteMatcher` patterns for unauthenticated access.
 * Keep in sync with `proxy.ts` via `getPublicRouteMatchers()`.
 */
export const STATIC_PUBLIC_ROUTE_PATTERNS = [
  "/",
  "/sign-in(.*)",
  "/sign-up(.*)",
  "/join",
  "/join/(.*)",
  "/og/parties/(.*)",
  "/recaps/(.*)",
  "/tv/(.*)",
  "/api/tv/(.*)",
  "/unsubscribe(.*)",
  "/api/unsubscribe(.*)",
  "/api/webhooks(.*)",
  "/api/cron(.*)",
  "/api/mcp(.*)",
  "/.well-known(.*)",
  "/robots.txt",
  "/sitemap.xml",
  "/llms.txt",
  "/manifest.json",
  "/shows",
  "/winners(.*)",
  "/predictions",
  "/predictions(.*)",
  "/award-shows(.*)",
  "/guides(.*)",
  "/blog(.*)",
  "/office-oscar-pool(.*)",
  // Partner reports. Share /reports/<token>. /reports/sabio stays for an emailed link.
  // /reports itself 404s.
  "/reports",
  "/reports/(.*)",
  "/privacy",
  "/terms",
  "/cookies",
  "/refunds",
  "/api/consent",
  "/api/mobile/release",
  "/api/mobile/apple-sign-in(.*)",
  "/checkout/return",
] as const;

export function getPublicRouteMatchers(): string[] {
  return [...STATIC_PUBLIC_ROUTE_PATTERNS, ...getAwardShowLandingPublicPaths()];
}

function normalizePathname(pathname: string): string {
  const withoutQuery = pathname.split("?")[0] ?? pathname;
  return withoutQuery.split("#")[0] ?? withoutQuery;
}

function patternToRegExp(pattern: string): RegExp {
  const withPlaceholder = pattern.replaceAll("(.*)", "__WILDCARD__");
  const escaped = withPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replaceAll("__WILDCARD__", ".*")}$`);
}

export function isPublicRoutePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return getPublicRouteMatchers().some((pattern) =>
    patternToRegExp(pattern).test(path),
  );
}

/** One anchored RegExp source equivalent to `isPublicRoutePath`, for inline scripts. */
export function getPublicRoutePathRegExpSource(): string {
  const alternatives = getPublicRouteMatchers().map((pattern) =>
    patternToRegExp(pattern).source.slice(1, -1),
  );
  return `^(?:${alternatives.join("|")})$`;
}

/**
 * Public pages a signed-out visitor can fully use without Clerk JS (TV display,
 * recaps, reports, invites, marketing). Their sign-in links go to /sign-in or
 * /sign-up, which still need Clerk and keep the load-failure recovery.
 */
export function isClerkOptionalPath(
  pathname: string | null | undefined,
): boolean {
  if (!pathname) {
    return false;
  }
  const path = normalizePathname(pathname);
  return isPublicRoutePath(path) && !isClerkCaptchaPath(path);
}

/** Public big-screen display. The signed-in host launcher lives under /parties. */
export function isTvKioskPath(pathname: string | null | undefined): boolean {
  if (!pathname) {
    return false;
  }
  const path = normalizePathname(pathname);
  return path === "/tv" || path.startsWith("/tv/");
}
